/**
 * The fixed-timestep movement simulation.
 *
 * On top of the plain Quake-style ground movement sits `state.momentum`: the
 * vauhti meter the HUD reports in km/h. It multiplies the speed the player
 * accelerates towards, climbs while he runs clean, and is bitten into by every
 * jump and every collision. See MOMENTUM in src/config/gameplay.ts.
 *
 * Alcohol reduces ground grip: velocity lasts longer through turns and braking.
 * The effect depends on dose, never on the run clock. Alcohol also slows the
 * local input spring on ground and in air; mouse look is immediate.
 * Beer still adds its own wish vector, velocity shove and speed boost.
 *
 * Camera sway lives in CameraController because it
 * must never influence where the player actually goes - that would make the
 * "camera sway intensity" setting a competitive advantage.
 *
 * No three.js here: this file is unit-testable in plain node.
 */

import { BEER } from '../config/beer';
import { DOUBLE_JUMP, GLASS_PAIN, LOOK, MOMENTUM, PLAYER, STEERING } from '../config/gameplay';
import { advanceBeer, createBeerState, resetBeerState, sampleBeer, type BeerState } from './Beer';
import type { IntoxicationSample } from './IntoxicationSystem';
import { BOOT_TAG, CollisionWorld, type MoveResult } from '../world/CollisionWorld';
import { clamp, clampLength2, length2, localToWorldXZ, vec2, vec3, type Vec2, type Vec3 } from '../utils/math';

export interface MovementInput {
  forward: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
  jump: boolean;
}

export interface PlayerState {
  position: Vec3;
  velocity: Vec3;
  yaw: number;
  pitch: number;
  /** Smoothed wish direction (local space). This is the inertia system. */
  steerDir: Vec2;
  steerVel: Vec2;
  grounded: boolean;
  timeSinceGrounded: number;
  /** Seconds of reduced steering and blocked jumps after a shove from Hannu. */
  staggerTimer: number;
  /**
   * Seconds of `GLASS_PAIN.slowFactor` left after going out through the
   * pirtti's window. Unlike `staggerTimer` this costs speed rather than
   * steering: the hands are fine, the legs are not.
   */
  hurtTimer: number;
  /**
   * The vauhti meter: a multiplier on `PLAYER.maxSpeed` in
   * [`MOMENTUM.min`, `MOMENTUM.max`]. Simulation state, not a display value -
   * it is what the HUD's km/h readout is actually reading.
   */
  momentum: number;
  /** Seconds of running since the last jump or collision. Drives the HUD streak. */
  cleanSeconds: number;
  /** The bottle: sips taken, the boost they bought, the lurch they cost. */
  beer: BeerState;
  /** Diagnostics for the debug overlay. */
  lastImpactSpeed: number;
  landedThisStep: boolean;
  jumpedThisStep: boolean;
  /** True on the single step the second press landed inside its window. */
  doubleJumpedThisStep: boolean;
  /**
   * The jump currently in the air, or null while on the ground.
   *
   * It carries what the take-off cost - because where a jump *lands* is what
   * decides whether it was charged for - and the two facts the second press
   * needs: how fast this jump left the ground, and how long ago. See
   * `AirJump` and the landing at the bottom of `stepPlayer`.
   */
  airJump: AirJump | null;
  /**
   * Whether the delayed space bar was down at the end of the previous step.
   *
   * The one piece of state the double jump needs and the ground jump does
   * not: a second jump has to be a fresh *press*, and a press is a rising
   * edge. Derived from the recorded button bit rather than sent alongside it,
   * so the replay format is untouched and a re-simulation finds the same
   * edges the live run did. See `DOUBLE_JUMP`.
   */
  jumpHeld: boolean;
  rawJumpHeld: boolean;
  /** Delay both edges so even a one-step tap survives the reaction time. */
  pendingJumpInputs: { held: boolean; remaining: number }[];
  /**
   * The tag of the surface underfoot, or null for plain ground.
   *
   * Kept on the state because the take-off needs it and `MoveResult` only
   * reports it after the move: a jump that leaves a car's boot lid is free,
   * and "am I standing on a lid" is a question asked at the moment space goes
   * down. It holds its last value through the air, which is also what makes a
   * hop taken inside `PLAYER.coyoteTime` off the edge of a lid free - stepping
   * off a car and jumping off one are the same thing to the player.
   */
  groundTag: string | null;
  /** Distance travelled on the ground, drives footstep audio later. */
  distanceTravelled: number;
}

/** The jump that is currently in the air. */
export interface AirJump {
  /** Meter taken by `MOMENTUM.jumpFactor`, as an amount rather than a factor. */
  momentum: number;
  /** The clean streak the take-off zeroed. */
  cleanSeconds: number;
  /**
   * The vertical speed this jump left the ground with.
   *
   * Kept rather than recomputed, because the meter it was read from is gone:
   * `breakMomentum` took its bite on the same step. It is what the second
   * press is measured against - both the size of the hop it gives back and
   * the clock its window runs on. See `DOUBLE_JUMP`.
   */
  takeoffVelocity: number;
  /** Seconds since take-off. The window is a multiple of the rise time. */
  seconds: number;
  /** Whether the one second press this jump is allowed has been spent. */
  doubled: boolean;
  /**
   * Took off from a boot lid, so nothing was charged for it and nothing has
   * to be handed back - whatever it lands on. See `MOMENTUM.jumpFactor`.
   */
  fromLid: boolean;
}

export function createPlayerState(position: Vec3, yaw: number): PlayerState {
  return {
    position: vec3(position.x, position.y, position.z),
    velocity: vec3(),
    yaw,
    pitch: 0,
    steerDir: vec2(),
    steerVel: vec2(),
    grounded: true,
    timeSinceGrounded: 0,
    staggerTimer: 0,
    hurtTimer: 0,
    momentum: MOMENTUM.min,
    cleanSeconds: 0,
    beer: createBeerState(),
    lastImpactSpeed: 0,
    landedThisStep: false,
    jumpedThisStep: false,
    doubleJumpedThisStep: false,
    airJump: null,
    jumpHeld: false,
    rawJumpHeld: false,
    pendingJumpInputs: [],
    groundTag: null,
    distanceTravelled: 0,
  };
}

export function resetPlayerState(state: PlayerState, position: Vec3, yaw: number): void {
  state.position.x = position.x;
  state.position.y = position.y;
  state.position.z = position.z;
  state.velocity.x = 0;
  state.velocity.y = 0;
  state.velocity.z = 0;
  state.yaw = yaw;
  state.pitch = 0;
  state.steerDir.x = 0;
  state.steerDir.y = 0;
  state.steerVel.x = 0;
  state.steerVel.y = 0;
  state.grounded = true;
  state.timeSinceGrounded = 0;
  state.staggerTimer = 0;
  state.hurtTimer = 0;
  state.momentum = MOMENTUM.min;
  state.cleanSeconds = 0;
  resetBeerState(state.beer);
  state.lastImpactSpeed = 0;
  state.landedThisStep = false;
  state.jumpedThisStep = false;
  state.doubleJumpedThisStep = false;
  state.airJump = null;
  state.jumpHeld = false;
  state.rawJumpHeld = false;
  state.pendingJumpInputs.length = 0;
  state.groundTag = null;
  state.distanceTravelled = 0;
}

/** Applies a raw mouse delta in pixels. Not part of the fixed step. */
export function applyLook(state: PlayerState, dx: number, dy: number, sensitivity: number): void {
  const scale = LOOK.baseSensitivity * sensitivity;
  state.yaw -= dx * scale;
  state.pitch = clamp(state.pitch - dy * scale, LOOK.minPitch, LOOK.maxPitch);
  // Keep yaw in a sane range so the debug overlay stays readable.
  if (state.yaw > Math.PI) state.yaw -= Math.PI * 2;
  if (state.yaw < -Math.PI) state.yaw += Math.PI * 2;
}

/**
 * A shove from outside the player's own control (currently only Hannu).
 * Applied straight to velocity so it reads as being hit, plus a short window of
 * reduced steering so recovering costs you something.
 *
 * It also costs vauhti. The velocity it hands you is free; the *ceiling* you
 * are accelerating back towards is not, which is what keeps a bated swipe a
 * trade rather than a straight gift.
 */
export function applyKnockback(
  state: PlayerState,
  directionX: number,
  directionZ: number,
  speed: number,
  lift: number,
  staggerSeconds: number,
): void {
  const length = Math.hypot(directionX, directionZ);
  if (length < 1e-6) return;
  state.velocity.x += (directionX / length) * speed;
  state.velocity.z += (directionZ / length) * speed;
  if (state.grounded) state.velocity.y = Math.max(state.velocity.y, lift);
  else state.velocity.y = Math.min(state.velocity.y, 0);
  // A swipe spoils the jump, including its second press and boot-lid refund.
  state.airJump = null;
  state.staggerTimer = Math.max(state.staggerTimer, staggerSeconds);
  breakMomentum(state, MOMENTUM.swipeFactor);
}

/**
 * Knocks the vauhti meter down and restarts the clean streak.
 *
 * Everything that counts as "touching something" goes through here, so the
 * meter can only ever be lost in one place.
 */
function breakMomentum(state: PlayerState, factor: number): void {
  state.momentum = Math.max(MOMENTUM.min, state.momentum * factor);
  state.cleanSeconds = 0;
}

function requestedDirection(input: MovementInput): Vec2 {
  const dir = vec2(
    (input.right ? 1 : 0) - (input.left ? 1 : 0),
    (input.forward ? 1 : 0) - (input.back ? 1 : 0),
  );
  const len = length2(dir);
  if (len > 1) {
    dir.x /= len;
    dir.y /= len;
  }
  return dir;
}

/** Quake-style ground friction: proportional above `stopSpeed`, flat below it. */
function applyFriction(state: PlayerState, dt: number, grip: number): void {
  const speed = Math.hypot(state.velocity.x, state.velocity.z);
  if (speed < 1e-5) {
    state.velocity.x = 0;
    state.velocity.z = 0;
    return;
  }
  const control = speed < PLAYER.stopSpeed ? PLAYER.stopSpeed : speed;
  const newSpeed = Math.max(0, speed - control * PLAYER.friction * grip * dt);
  const scale = newSpeed / speed;
  state.velocity.x *= scale;
  state.velocity.z *= scale;
}

/** Quake-style directional acceleration: only adds speed along the wish dir. */
function accelerate(
  state: PlayerState,
  wishX: number,
  wishZ: number,
  wishSpeed: number,
  accel: number,
  dt: number,
): void {
  if (wishSpeed <= 1e-5) return;
  const current = state.velocity.x * wishX + state.velocity.z * wishZ;
  const addSpeed = wishSpeed - current;
  if (addSpeed <= 0) return;
  const accelSpeed = Math.min(accel * wishSpeed * dt, addSpeed);
  state.velocity.x += wishX * accelSpeed;
  state.velocity.z += wishZ * accelSpeed;
}

/**
 * Advances the player by exactly one fixed timestep.
 *
 * @param intox sample taken at the *current* simulation time
 * @param canMove false while the run is READY/FINISHED - gravity still applies
 */
export function stepPlayer(
  state: PlayerState,
  input: MovementInput,
  intox: IntoxicationSample,
  world: CollisionWorld,
  dt: number,
  canMove: boolean,
): MoveResult {
  state.landedThisStep = false;
  state.jumpedThisStep = false;
  state.doubleJumpedThisStep = false;

  // Capture the dose at each edge; a later sip must not restart a pending tap.
  // A held button still repeats ground jumps, but never creates a second press.
  let jump = state.jumpHeld;
  if (!canMove) {
    state.pendingJumpInputs.length = 0;
    state.rawJumpHeld = false;
    jump = false;
  } else if (state.staggerTimer > 0) {
    // Consume blocked presses so neither alcohol delay nor held space queues
    // a take-off as soon as the shove ends. Recovery needs a fresh press.
    state.pendingJumpInputs.length = 0;
    state.rawJumpHeld = input.jump;
    jump = false;
  } else {
    for (const pending of state.pendingJumpInputs) pending.remaining -= dt;
    if (input.jump !== state.rawJumpHeld) {
      state.pendingJumpInputs.push({ held: input.jump, remaining: intox.jumpDelay });
      state.rawJumpHeld = input.jump;
    }
    while (state.pendingJumpInputs.length && state.pendingJumpInputs[0].remaining <= 1e-9) {
      jump = state.pendingJumpInputs.shift()!.held;
    }
  }
  const jumpPressed = jump && !state.jumpHeld;

  const requested = canMove ? requestedDirection(input) : vec2();

  // Staggered: the input still counts, just not for much.
  if (state.staggerTimer > 0) {
    const authority = PLAYER.staggerControl;
    requested.x *= authority;
    requested.y *= authority;
    state.staggerTimer = Math.max(0, state.staggerTimer - dt);
  }

  // Cut to bits on the way out of the window. Read here and spent here, so the
  // step that starts it is the first slow step rather than a free one - see
  // `GLASS_PAIN`. The steering is untouched; only the ceiling comes down.
  const hurt = state.hurtTimer > 0 ? GLASS_PAIN.slowFactor : 1;
  // Counted down the same way `staggerTimer` is, floating-point residue and
  // all: 360 subtractions of 1/120 leave 8e-15 rather than 0, so the cut
  // outlives its nominal three seconds by one step. Deterministic, identical
  // on every machine, and eight milliseconds - not worth an epsilon that the
  // timer next to it does not have.
  if (state.hurtTimer > 0) state.hurtTimer = Math.max(0, state.hurtTimer - dt);

  /* -- the beer ---------------------------------------------------------- */
  //
  // Sampled before anything reads the wish vector, spent after everything has.
  // Steering authority is scaled by how hard the lurch is hitting rather than
  // switched off for its whole length: a weak sip should cost less than a
  // strong one, and the moment the bottle comes down you have your feet back.
  const beer = sampleBeer(state.beer);
  if (beer.intensity > 0) {
    const authority = 1 - (1 - BEER.control) * Math.min(1, beer.intensity);
    requested.x *= authority;
    requested.y *= authority;
  }

  const target = clampLength2(
    vec2(requested.x + beer.wish.x, requested.y + beer.wish.y),
    PLAYER.maxWishLength,
  );

  /* -- input smoothing (spring-damper) ---------------------------------- */
  // Stretch the response while preserving damping, so more booze always makes
  // starts, releases and direction changes slower without adding oscillation.
  const frequency = STEERING.frequency / intox.responseTimeScale;
  const k = frequency * frequency;
  const c = 2 * STEERING.damping * frequency;
  state.steerVel.x += ((target.x - state.steerDir.x) * k - state.steerVel.x * c) * dt;
  state.steerVel.y += ((target.y - state.steerDir.y) * k - state.steerVel.y * c) * dt;
  state.steerDir.x += state.steerVel.x * dt;
  state.steerDir.y += state.steerVel.y * dt;
  const clamped = clampLength2(state.steerDir, PLAYER.maxWishLength);
  state.steerDir.x = clamped.x;
  state.steerDir.y = clamped.y;

  /* -- the vauhti meter -------------------------------------------------- */
  //
  // Built on the ground only: a long jump is not a way to bank free momentum,
  // and it has to be the running that pays.
  const groundSpeed = Math.hypot(state.velocity.x, state.velocity.z);
  if (state.grounded) {
    if (canMove && groundSpeed >= MOMENTUM.buildMinSpeed) {
      state.cleanSeconds += dt;
      state.momentum = Math.min(MOMENTUM.max, state.momentum + MOMENTUM.buildPerSecond * dt);
    } else {
      state.cleanSeconds = 0;
      state.momentum = Math.max(MOMENTUM.min, state.momentum - MOMENTUM.idleDecayPerSecond * dt);
    }
  }

  /* -- turn the local wish vector into world space ----------------------- */
  const world2d = localToWorldXZ(state.steerDir, state.yaw);
  const wishLength = Math.hypot(world2d.x, world2d.z);
  // The beer boost rides on top of the earned meter, which is the whole reason
  // it is worth drinking at all: it is the only way past `MOMENTUM.max`.
  const wishSpeed = wishLength * PLAYER.maxSpeed * (state.momentum + state.beer.boost) * hurt;
  const wishX = wishLength > 1e-6 ? world2d.x / wishLength : 0;
  const wishZ = wishLength > 1e-6 ? world2d.z / wishLength : 0;

  if (state.grounded) {
    // World-space velocity retains its old heading even when the mouse turns.
    // Matching traction to friction keeps the straight-line speed target intact.
    applyFriction(state, dt, intox.groundGrip);
    accelerate(state, wishX, wishZ, wishSpeed, PLAYER.groundAccel * intox.groundGrip, dt);
  } else {
    accelerate(state, wishX, wishZ, wishSpeed, PLAYER.airAccel, dt);
  }

  /* -- sip shoves straight into velocity -------------------------------- */
  if (canMove) {
    const shove = localToWorldXZ(beer.accel, state.yaw);
    state.velocity.x += shove.x * dt;
    state.velocity.z += shove.z * dt;
  }

  /* -- jump + gravity ---------------------------------------------------- */
  //
  // The ground jump reads the bar as *held*, exactly as it always has. Only
  // the second one wants an edge - see `DOUBLE_JUMP` for why the two are
  // deliberately not the same question.
  const canJump =
    canMove && jump && state.timeSinceGrounded <= PLAYER.coyoteTime && state.velocity.y <= 0.1;
  if (canJump) {
    // Read *before* breakMomentum takes its bite: the hop you get is the vauhti
    // you arrived with, not what is left of it afterwards.
    const takeoff = jumpVelocity(state.momentum);
    state.velocity.y = takeoff;
    state.grounded = false;
    state.timeSinceGrounded = PLAYER.coyoteTime + 1;
    state.jumpedThisStep = true;
    // Off a boot lid there is nothing to charge and nothing to hand back
    // later: getting down off a car is part of getting over it. Everywhere
    // else both halves are deliberate - the multiplier is the lasting cost,
    // the velocity bite is what makes the km/h readout drop under your feet
    // the instant you press space.
    const fromLid = state.groundTag === BOOT_TAG;
    const meterBefore = state.momentum;
    const streakBefore = state.cleanSeconds;
    if (!fromLid) {
      breakMomentum(state, MOMENTUM.jumpFactor);
      state.velocity.x *= MOMENTUM.jumpSpeedFactor;
      state.velocity.z *= MOMENTUM.jumpSpeedFactor;
    }
    // Charged now, refundable on landing: see the bottom of this function.
    state.airJump = {
      momentum: meterBefore - state.momentum,
      cleanSeconds: streakBefore,
      takeoffVelocity: takeoff,
      seconds: 0,
      doubled: false,
      fromLid,
    };
  } else if (canMove && jumpPressed && state.airJump && !state.airJump.doubled) {
    // The second press. Inside its window it puts the legs back under you
    // once; outside it, nothing happens and nothing is spent - so an early
    // press may be released and tried again inside the same jump.
    const air = state.airJump;
    const rise = air.takeoffVelocity / PLAYER.gravity;
    if (air.seconds >= rise * DOUBLE_JUMP.windowOpen && air.seconds <= rise * DOUBLE_JUMP.windowClose) {
      air.doubled = true;
      state.doubleJumpedThisStep = true;
      // `max`, not `+=`: the hop is a floor under the fall rather than
      // something added to whatever is left of the first jump. Adding would
      // put the peak over `jumpApex`, which is the one thing this may not do.
      state.velocity.y = Math.max(state.velocity.y, air.takeoffVelocity * DOUBLE_JUMP.velocityFactor);
      // And its own price, taken the same two ways the take-off's is - but
      // *not* added to `air.momentum`, so a lid landing hands back the jump
      // and never the double. The runner who timed his take-off right still
      // gets the lot for free; the one who needed a second chance pays for it.
      breakMomentum(state, DOUBLE_JUMP.momentumFactor);
      state.velocity.x *= DOUBLE_JUMP.speedFactor;
      state.velocity.z *= DOUBLE_JUMP.speedFactor;
    }
  }
  if (state.airJump) state.airJump.seconds += dt;
  state.velocity.y -= PLAYER.gravity * dt;

  /* -- integrate + collide ----------------------------------------------- */
  const before = { x: state.position.x, z: state.position.z };
  const result = world.moveCylinder(
    state.position,
    state.velocity,
    PLAYER.radius,
    PLAYER.height,
    dt,
  );

  // Running into something. `impactSpeed` is the speed the wall took off you
  // along its own normal, so brushing past one at a shallow angle stays free
  // and only a genuine crash is charged for.
  if (result.impactSpeed >= MOMENTUM.hitImpactSpeed) {
    breakMomentum(state, MOMENTUM.hitFactor);
    state.velocity.x *= MOMENTUM.hitSpeedFactor;
    state.velocity.z *= MOMENTUM.hitSpeedFactor;
  }

  // Last, so everything above saw the sip exactly as it stood at the top of
  // the step. A sip taken *this* step is sampled at envelope 0 and starts to
  // bite on the next one.
  advanceBeer(state.beer, dt);

  const wasGrounded = state.grounded;
  state.grounded = result.grounded;
  state.timeSinceGrounded = result.grounded ? 0 : state.timeSinceGrounded + dt;
  state.landedThisStep = !wasGrounded && result.grounded;
  state.lastImpactSpeed = result.impactSpeed;

  // A jump with a car's boot lid at either end of it was free.
  //
  // The lot is the one stretch of the route where leaving the ground is the
  // fast line rather than a tax on it, and a hurdle that charges you the exact
  // meter you had to earn to clear it is one nobody takes twice: you arrive at
  // the next car slower than the runner who went round. So the take-off price
  // is not written off when it is taken - it is held on the state and handed
  // back in full the moment the landing turns out to be a lid. Meter, clean
  // streak and the velocity bite alike, because a jump that still showed up on
  // the km/h readout would not read as free whatever the meter said. A jump
  // that *left* a lid was never charged at all, so there is nothing to give
  // back - only the airtime below.
  //
  // It is also what makes the last two cars a pair rather than two hurdles:
  // the second hop leaves the first lid on the vauhti that got you onto it,
  // not on `jumpFactor` of it. Land anywhere else, from anywhere else, and the
  // price stands.
  //
  // The airtime is the other half of "free", and the half that used to be
  // missing. The meter builds with your feet down and nowhere else, so a vault
  // that handed the whole take-off back still cost the build that the same
  // half-second of running would have earned - which is a car that slows you
  // down however loudly the refund is announced. So a free jump is paid
  // `buildPerSecond` for the time it was off the ground, at exactly the ground
  // rate, and a landing on a lid is paid `vaultBonus` on top for having been
  // the hard one to hit.
  //
  // The double jump is deliberately not in any of this: `DOUBLE_JUMP` takes
  // its own bite outside `airJump.momentum`, so a second press costs the same
  // over a car as it does anywhere else.
  if (state.airJump && result.grounded) {
    const air = state.airJump;
    const ontoLid = result.groundTag === BOOT_TAG;
    if (ontoLid || air.fromLid) {
      if (!air.fromLid) {
        state.momentum = Math.min(MOMENTUM.max, state.momentum + air.momentum);
        state.cleanSeconds = air.cleanSeconds;
        state.velocity.x /= MOMENTUM.jumpSpeedFactor;
        state.velocity.z /= MOMENTUM.jumpSpeedFactor;
      }
      const paid = MOMENTUM.buildPerSecond * air.seconds + (ontoLid ? MOMENTUM.vaultBonus : 0);
      state.momentum = Math.min(MOMENTUM.max, state.momentum + paid);
      state.cleanSeconds += air.seconds;
    }
    state.airJump = null;
  }
  // Held rather than cleared in the air, so a hop taken inside `coyoteTime`
  // off the edge of a lid is the free one it looks like.
  if (result.grounded) state.groundTag = result.groundTag;
  // Last thing in the step, so `jumpPressed` above saw the bar as it stood on
  // the way in. A jump that landed this step has already cleared `airJump`,
  // so a held bar cannot double on the next one either.
  state.jumpHeld = jump;
  if (result.grounded) {
    state.distanceTravelled += Math.hypot(
      state.position.x - before.x,
      state.position.z - before.z,
    );
  }

  return result;
}

/**
 * Take-off velocity at a given vauhti.
 *
 * The meter is a multiplier on how fast you may run; this is the one place it
 * is also a multiplier on how high you may get off the ground. A cold jump is
 * `PLAYER.jumpVelocity` exactly, so nothing about the pirtti, the window or the
 * sleeping LAN players moved - the level's opening minute is run at
 * `MOMENTUM.min` and never sees this. What it changes is the end of the
 * parkkipaikka, where the meter is full and a car's boot lid is inside reach.
 *
 * Exported because the level tests assert on the apex directly: what a car may
 * and may not be hopped at is a property of the level, and a test that
 * recomputed the formula would only ever agree with itself.
 */
export function jumpVelocity(momentum: number): number {
  const earned = Math.max(0, momentum - MOMENTUM.min);
  return PLAYER.jumpVelocity * (1 + MOMENTUM.jumpBoost * earned);
}

/** How high the feet get at that vauhti, from a standing surface. */
export function jumpApex(momentum: number): number {
  const v = jumpVelocity(momentum);
  return (v * v) / (2 * PLAYER.gravity);
}

/** Horizontal speed in m/s, used by the HUD, debug overlay and head bob. */
export function horizontalSpeed(state: PlayerState): number {
  return Math.hypot(state.velocity.x, state.velocity.z);
}

/**
 * The vauhti meter as 0..1, for the HUD bar. `MOMENTUM.min` reads as empty
 * rather than as "1/1.9 full", because what the bar is showing is how much of
 * the *earnable* range you have earned.
 */
export function momentumFraction(state: PlayerState): number {
  const span = MOMENTUM.max - MOMENTUM.min;
  if (span <= 0) return 0;
  return clamp((state.momentum - MOMENTUM.min) / span, 0, 1);
}
